import React, { useContext, useMemo, useState } from "react";
import { useLocalization } from "@fluent/react";
import {
  Checkbox,
  Chip,
  CollapsibleCard,
  Alert,
  Button,
  IconButton,
  TextInput,
} from "@narmi/design_system";
import { useNotificationContext } from "cerulean";
import { groupAccountsByOrganization } from "byzantine";
import Account from "byzantine/src/Account";
import cc from "classcat";
import InstitutionSettingsContext from "../contexts/InstitutionSettingsContext";
import AccountPermissionsEditor from "./AccountPermissionsEditor";
import CopyAccountPermissionsDialog from "./CopyAccountPermissionsDialog";
import {
  ACCOUNTS_ERROR,
  AccountPermissionState,
  DEFAULT_ACCOUNT_PERMISSION_STATE,
  accountNeedsSetup,
  isLoanAccount,
  countAccountPermissions,
} from "./RoleFormUtils";
import styles from "./OrganizationRoles.module.scss";

// Above this many accounts the list gets a search box, a select-all checkbox and scrolls.
export const LONG_ACCOUNT_LIST_THRESHOLD = 10;

type SelectableAccount = Account & { selected?: boolean };

interface RoleAccountsSectionProps {
  /** Injected by `ContextForm.Field`: the accounts from the form data, with a `selected` flag. */
  value?: SelectableAccount[];
  /** Injected by `ContextForm.Field`: replaces the whole accounts array. */
  onChange?: (accounts: SelectableAccount[]) => void;
  error?: string;
  /** Per-account access and scoped permissions, keyed by account uuid. */
  accountPermissions?: Record<string, AccountPermissionState>;
  onAccountPermissionsChange?: (
    accountUuid: string,
    next: AccountPermissionState
  ) => void;
  /**
   * Copies one account's access and permissions to other accounts, selecting them. Without it,
   * the section has no copy button.
   */
  onCopyAccountPermissions?: (
    sourceAccountId: string,
    targetAccountIds: string[]
  ) => void;
  features?: Record<string, any>;
  [key: string]: unknown;
}

/** Accounts a role covers: select accounts individually, search and select all for long lists. */
export default function RoleAccountsSection({
  value = [],
  onChange = () => {},
  error,
  accountPermissions = {},
  onAccountPermissionsChange = () => {},
  onCopyAccountPermissions,
  features = {},
}: RoleAccountsSectionProps) {
  const { l10n } = useLocalization();
  const { sendNotification } = useNotificationContext();
  const institution = useContext(InstitutionSettingsContext);
  const [query, setQuery] = useState("");
  const [copySource, setCopySource] = useState<SelectableAccount | null>(null);
  const [openAccountIds, setOpenAccountIds] = useState<Set<string>>(new Set());

  const setOpen = (accountId: string, open: boolean) =>
    setOpenAccountIds((previous) => {
      const next = new Set(previous);
      if (open) next.add(accountId);
      else next.delete(accountId);
      return next;
    });

  const groupedAccounts = useMemo(
    () =>
      groupAccountsByOrganization(
        value as unknown as Account[],
        institution?.long_name
      ) as unknown as Map<string, SelectableAccount[]>,
    [value, institution?.long_name]
  );
  const orgCount = groupedAccounts.size;
  const hasMultipleOrgs = orgCount > 1;
  const isLongList = value.length > LONG_ACCOUNT_LIST_THRESHOLD;

  const normalizedQuery = query.trim().toLowerCase();
  const matchesQuery = (account: SelectableAccount) =>
    !normalizedQuery ||
    account.getShortDescription().toLowerCase().includes(normalizedQuery);

  const setSelected = (shouldSelect: (account: SelectableAccount) => boolean) =>
    onChange(
      value.map((previous) => {
        const account = new Account(
          previous as Partial<any>
        ) as SelectableAccount;
        account.selected = shouldSelect(previous);
        return account;
      })
    );

  const toggleAccount = (toggled: SelectableAccount) =>
    setSelected((account) =>
      String(account.id) === String(toggled.id)
        ? !account.selected
        : Boolean(account.selected)
    );

  const allSelected = value.length > 0 && value.every((a) => a.selected);

  const copyPermissions = (targetAccountIds: string[]) => {
    if (!copySource || !onCopyAccountPermissions) return;
    onCopyAccountPermissions(String(copySource.id), targetAccountIds);
    setCopySource(null);
    sendNotification({
      type: "success",
      text: l10n.getString("org-role-copy-success", {
        count: targetAccountIds.length,
      }),
    });
  };

  const renderAccount = (account: SelectableAccount) => {
    const accountId = String(account.id);
    // An account the role can't access has nothing to configure, so it can't be expanded.
    const hasAccess = Boolean(account.selected);
    const isOpen = hasAccess && openAccountIds.has(accountId);
    const permissionState =
      accountPermissions[accountId] ?? DEFAULT_ACCOUNT_PERMISSION_STATE;
    // A loan account only offers transfers, so there is little to copy from it.
    const isLoan = isLoanAccount(account);
    const canCopy =
      Boolean(onCopyAccountPermissions) && !isLoan && value.length > 1;
    const needsSetup = accountNeedsSetup(account, permissionState);

    return (
      <div
        key={account.id}
        className={cc({
          [styles.accountCardNoAccess]: !hasAccess,
          // After a failed save, highlight the accounts that still need permissions
          [styles.accountCardError]:
            needsSetup && error === ACCOUNTS_ERROR.NEEDS_SETUP,
        })}
      >
        <CollapsibleCard
          isOpen={isOpen}
          trigger="caret-end"
          onOpen={() => setOpen(accountId, true)}
          onClose={() => setOpen(accountId, false)}
          renderTitle={() => (
            <div
              className={cc([
                styles.accountCardHeader,
                { [styles.accountCardHeaderOpen]: isOpen },
              ])}
            >
              <Checkbox
                kind="condensed"
                label={
                  (
                    <span className={styles.accountLabel}>
                      <h4 className={styles.accountName}>
                        {account.getName()}
                      </h4>
                      <span className={styles.accountMask}>
                        {account.getMaskedNumber()}
                      </span>
                    </span>
                  ) as unknown as string
                }
                checked={hasAccess}
                onChange={() => toggleAccount(account)}
              />
              {!hasAccess && (
                <span className={styles.accountNoAccess}>
                  {l10n.getString("org-role-account-no-access")}
                </span>
              )}
              {canCopy && isOpen && (
                <span
                  className={styles.accountCopyButton}
                  // The header toggles the card, so a click here must not reach it.
                  onClick={(e) => e.stopPropagation()}
                  onKeyDown={(e) => e.stopPropagation()}
                  role="presentation"
                >
                  <Button
                    kind="plain"
                    size="xs"
                    type="button"
                    onClick={() => setCopySource(account)}
                  >
                    {l10n.getString("org-role-copy-to-other-accounts")}
                  </Button>
                </span>
              )}
              {needsSetup && (
                <span className={styles.accountStatusChip}>
                  <Chip
                    kind="info"
                    label={l10n.getString("org-role-account-needs-setup")}
                  />
                </span>
              )}
              {hasAccess && !needsSetup && (
                <span className={styles.accountStatusChip}>
                  <Chip
                    kind="success"
                    label={
                      permissionState.access === "view_only"
                        ? l10n.getString("org-role-account-access-view-only")
                        : l10n.getString("org-role-account-permission-count", {
                            count: countAccountPermissions(
                              permissionState,
                              features
                            ),
                          })
                    }
                  />
                </span>
              )}
              {hasAccess && (
                <span className={styles.accountChevron}>
                  <IconButton
                    type="button"
                    kind="action"
                    label={isOpen ? "Collapse" : "Expand"}
                    name={isOpen ? "chevron-up" : "chevron-down"}
                    textSize="l"
                    onClick={() => setOpen(accountId, !isOpen)}
                  />
                </span>
              )}
            </div>
          )}
        >
          <AccountPermissionsEditor
            accountUuid={accountId}
            value={permissionState}
            onChange={(next) => onAccountPermissionsChange(accountId, next)}
            features={features}
            isLoan={isLoan}
          />
        </CollapsibleCard>
      </div>
    );
  };

  const groups = hasMultipleOrgs
    ? Array.from(groupedAccounts.entries())
    : [["", value] as [string, SelectableAccount[]]];
  const visibleGroups = groups
    .map(
      ([orgName, accounts]) => [orgName, accounts.filter(matchesQuery)] as const
    )
    .filter(([, accounts]) => accounts.length > 0);

  const needsSetupCount = value.filter((account) =>
    accountNeedsSetup(
      account,
      accountPermissions[String(account.id)] ?? DEFAULT_ACCOUNT_PERMISSION_STATE
    )
  ).length;
  let errorMessage: string | null = null;
  if (error === ACCOUNTS_ERROR.NONE_SELECTED) {
    errorMessage = l10n.getString("org-role-error-no-accounts");
  } else if (error === ACCOUNTS_ERROR.NEEDS_SETUP && needsSetupCount > 0) {
    errorMessage = l10n.getString("org-role-error-accounts-need-setup", {
      count: needsSetupCount,
    });
  }

  return (
    <section className={cc([styles.section, styles.firstSection])}>
      <h4 className={styles.sectionTitle}>
        {l10n.getString("org-role-section-accounts")}
      </h4>
      <p className={styles.sectionDescription}>
        {l10n.getString("org-role-accounts-description")}
      </p>
      <div className={cc({ [styles.accountsError]: Boolean(errorMessage) })}>
        <Alert
          kind="error"
          isActive={Boolean(errorMessage)}
          isDismissable={false}
        >
          {errorMessage}
        </Alert>
      </div>
      {isLongList && (
        <div className={styles.accountsToolbar}>
          <div className={styles.accountsSearch}>
            <TextInput
              label={l10n.getString("org-role-accounts-search-placeholder")}
              value={query}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setQuery(e.target.value)
              }
              startIcon="search"
            />
          </div>
          <span className={styles.accountsCount}>
            {hasMultipleOrgs
              ? l10n.getString("org-role-accounts-count-multi-org", {
                  count: value.length,
                  orgs: orgCount,
                })
              : l10n.getString("org-role-accounts-count", {
                  count: value.length,
                })}
          </span>
          <Checkbox
            label={l10n.getString("org-role-accounts-select-all", {
              count: value.length,
            })}
            checked={allSelected}
            onChange={() => setSelected(() => !allSelected)}
          />
        </div>
      )}
      <div
        className={cc({
          [styles.accountsListScrollable]: isLongList,
        })}
      >
        {visibleGroups.map(([orgName, accounts]) => (
          <div key={orgName || "accounts"} className={styles.orgGroupContainer}>
            {orgName && <div className={styles.orgGroupName}>{orgName}</div>}
            <div className={styles.accountsList}>
              {accounts.map(renderAccount)}
            </div>
          </div>
        ))}
        {visibleGroups.length === 0 && (
          <div className={styles.noResults}>
            {l10n.getString("org-role-accounts-no-results")}
          </div>
        )}
      </div>
      <CopyAccountPermissionsDialog
        source={copySource}
        accounts={value}
        institutionName={institution?.long_name}
        onClose={() => setCopySource(null)}
        onCopy={copyPermissions}
      />
    </section>
  );
}
